import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import 'bootstrap/dist/css/bootstrap.css';

const fallbackBanner = 'assets/images/service-provider/rent-car/banner.jpg';

/* [CF 2026-08-09 #4] Chevrons drawn as SVG on purpose. The old ‹ / › glyphs
   (U+2039 / U+203A) are BIDI-MIRRORED characters: on an Arabic page the
   browser flips them, so the button on the left rendered a right-pointing
   arrow — which is what the client reported as "the arrow directions are
   reversed". An SVG path is never mirrored by the bidi algorithm. */
const Chevron = ({ dir }) => (
  <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" focusable="false">
    <path
      d={dir === 'left' ? 'M15 4 L7 12 L15 20' : 'M9 4 L17 12 L9 20'}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CommonBanner = ({ bannerImages }) => {
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const { i18n } = useTranslation();
  const isRTL = (i18n.language || 'en').toLowerCase().startsWith('ar');
  const imgs = bannerImages || [];
  const n = imgs.length;

  /* Previous sits on the inline-START edge and next on the inline-END edge, so
     in Arabic the pair mirrors along with the page and the images advance
     leftwards the way the rest of the layout reads. */
  const step = useCallback(
    (delta) => setLightboxIdx((i) => (i === null ? i : (i + delta + n) % n)),
    [n]
  );

  // Keyboard for desktop; Escape closes.
  useEffect(() => {
    if (lightboxIdx === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxIdx(null);
      else if (e.key === 'ArrowRight') step(isRTL ? -1 : 1);
      else if (e.key === 'ArrowLeft') step(isRTL ? 1 : -1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [lightboxIdx, isRTL, step]);

  /* The lightbox had no touch handling at all, so on a phone the only way
     through the banners was the arrows (client: "swiping left or right does
     not move to the next banner"). Track the gesture ourselves — a horizontal
     drag past 45px, and clearly more horizontal than vertical, pages. */
  const touch = useRef(null);
  const onTouchStart = (e) => {
    const p = e.touches && e.touches[0];
    touch.current = p ? { x: p.clientX, y: p.clientY } : null;
  };
  const onTouchEnd = (e) => {
    const start = touch.current;
    touch.current = null;
    if (!start || n < 2) return;
    const p = e.changedTouches && e.changedTouches[0];
    if (!p) return;
    const dx = p.clientX - start.x;
    const dy = p.clientY - start.y;
    if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy)) return;
    // Dragging toward the inline-end edge reveals the next image.
    const forward = isRTL ? dx > 0 : dx < 0;
    step(forward ? 1 : -1);
  };
  const galClass =
    n === 1
      ? 'cav-gallery--1'
      : n === 2
        ? 'cav-gallery--2'
        : n === 3
          ? 'cav-gallery--3'
          : 'cav-gallery--many';

  return (
    <div className="container cav-gallery-wrap">
      {lightboxIdx !== null && imgs[lightboxIdx] && (
        <div
          className="cav-lightbox"
          onClick={() => setLightboxIdx(null)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <button
            className="cav-lb-close"
            aria-label="Close"
            onClick={() => setLightboxIdx(null)}
          >
            &times;
          </button>
          {n > 1 && (
            <button
              className="cav-lb-prev"
              aria-label="Previous"
              onClick={(e) => {
                e.stopPropagation();
                step(-1);
              }}
            >
              <Chevron dir={isRTL ? 'right' : 'left'} />
            </button>
          )}
          <img
            src={imgs[lightboxIdx].original}
            alt=""
            onClick={(e) => e.stopPropagation()}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = fallbackBanner;
            }}
          />
          {n > 1 && (
            <button
              className="cav-lb-next"
              aria-label="Next"
              onClick={(e) => {
                e.stopPropagation();
                step(1);
              }}
            >
              <Chevron dir={isRTL ? 'left' : 'right'} />
            </button>
          )}
          {n > 1 && (
            /* dir="ltr" — "1 / 3" is a number pair, and in an RTL paragraph the
               bidi algorithm reorders it to read "3 / 1". */
            <div className="cav-lb-count" dir="ltr">
              {lightboxIdx + 1} / {n}
            </div>
          )}
        </div>
      )}
      <div className={`cav-gallery ${galClass}`}>
        {/* [QA] Mobile swipe arrow hints — only meaningful when >1 image.
            Visibility is fully CSS-controlled (hidden on desktop). They are
            non-interactive hints; the gallery itself is the scroll surface. */}
        {n > 1 && (
          <>
            <span className="cav-swipe-arrow cav-swipe-arrow-left" aria-hidden="true">&#8249;</span>
            <span className="cav-swipe-arrow cav-swipe-arrow-right" aria-hidden="true">&#8250;</span>
          </>
        )}
        {imgs.map((item, i) => (
          <div
            className="cav-gallery-cell"
            key={i}
            onClick={() => setLightboxIdx(i)}
          >
            <img
              src={item.original}
              alt=""
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = fallbackBanner;
              }}
            />
            {n > 5 && i === 4 && (
              <div className="cav-gallery-more">
                <span>+{n - 5}</span>
                <span>See all {n} photos</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
